/**
 * 自由出行·商业街店面（2026-10-12 C 批，由 `FreeroamMap.openPartShop/openGiftShop` 拆出）
 *
 * 一个文件两个组件：`PartShop`（发艺/服饰/珠宝/医美四家，同一套编辑器骨架 + 价签购买流）与
 * `GiftShop`（心意礼品店清单）。承载层都是覆盖层内的全屏 `.fr-shop-full`，DOM 结构与拆出前一致
 * （店头 → 预览舞台 → 通栏底栏 `.cc-controls`，脚部 `.fr-shop-foot` 挂在底栏内）。
 *
 * 两条硬口径：
 * ① 落档不许直写 `player.avatar`——一律 `GameState.wearDraft`（逐槽按 owned 过滤 + 联动槽放行）。
 *    旧写法把预览过的未买件一起穿上，再经 `ensureOwned` 洗白成永久拥有（实测 0 元到手）。
 * ② 钱不够＝`.blocked` 置灰**但点得动** + 一句「还差 ¥X」（10-04 第六令；别用原生 disabled 会吞点击）。
 */
import { GameState } from '../systems/GameState';
import { Utils } from '../core/Utils';
import { GIFTS } from '../data/tables/Gifts';
import { LOCATION_MAP } from '../data/tables/Locations';
import { AvatarConfig, sanitizeConfig } from '../avatar/config';
import { FACE_CATEGORIES, FaceCategory } from '../avatar/categories';
import { ensureSprites } from '../avatar/gamemount';
import { AvatarEditor } from './AvatarEditor';
import { icon } from './icons';
import { createButton, createDiv, createSpan, showHint } from './ui';

/**
 * 部件价签（⚠️ 占位价——价目表归《自由出行文案填写表》批，茄子改定后只改这一张表 不许改 id）。
 * 医美=无门纯高价（改脸远贵于买衣）；发型/服装/配饰按档直觉给占位。
 */
const PART_PRICES: Record<string, number> = {
    face: 30000, brow: 20000, eye: 30000, nose: 20000, mouth: 20000, skin: 15000, eyeColor: 10000,
    hairFront: 5000, hairSides: 5000, hairBack: 5000, hairColor: 1000,
    cloth: 5000, glasses: 2000, acc: 3000,
};

/** 店 → 可购类目（蒂尔玛=五官/肤色/瞳色全游戏唯一窗口） */
export const SHOP_CATS: Record<string, string[]> = {
    shop_hair: ['hairFront', 'hairSides', 'hairBack', 'hairColor'],
    shop_cloth: ['cloth'],
    shop_acc: ['glasses', 'acc'],
    shop_derma: ['face', 'brow', 'eye', 'nose', 'mouth', 'skin', 'eyeColor'],
};

/** 查价：类目漏填=按 9999 卖是静默错账，当场报出来（兜底要响） */
function priceOf(catKey: string): number {
    const p = PART_PRICES[catKey];
    if (p === undefined) console.warn(`[店面] 类目 ${catKey} 没进 PART_PRICES，暂按 ¥9999 卖`);
    return p ?? 9999;
}

/** 部件条角标：站内价格钉死整千档（不许随机浮动零头），出零头=表填错了 */
function priceTag(price: number): string {
    if (price % 1000 !== 0) {
        console.warn(`[店面] 价签 ${price} 不是整千，k 记法会出小数——检查 PART_PRICES`);
        return `¥${Utils.formatMoney(price)}`;
    }
    return `¥${price / 1000}k`;
}

/** 店头：标题居中 + 金钱读数绝对居右（信息栏同款 icon + 「金钱 ¥xx」），返回读数 span 供刷新 */
function buildShopHead(inner: HTMLElement, title: string): HTMLElement {
    const head = createDiv('studio-head');
    head.appendChild(createSpan('head-title', title));
    const wrap = createDiv('fr-shop-money');
    wrap.appendChild(icon('money'));
    const moneyNum = createSpan('fr-shop-money-num');
    wrap.appendChild(moneyNum);
    head.appendChild(wrap);
    inner.appendChild(head);
    return moneyNum;
}

const moneyText = (): string => `金钱 ¥${Utils.formatMoney(GameState.getInstance().getMoney())}`;

/**
 * 钱不够的现成配方（10-04 第六令）：置灰但点得动，点按只报差额；够钱才走 onOk。
 * 差额在**点按那一刻**重算，并返回同步闭包——店面是多钮同屏（礼品店 12 颗），
 * 买掉一件后其余钮的置灰要跟着当前余额重判；把差额在接线时定死会漏成负余额。
 */
function wirePayButton(btn: HTMLButtonElement, price: number, onOk: () => void): () => void {
    const sync = (): void => {
        btn.classList.toggle('blocked', price - GameState.getInstance().getMoney() > 0);
    };
    btn.onclick = (e) => {
        e.stopPropagation();
        const short = price - GameState.getInstance().getMoney();
        if (short > 0) { showHint(`还差 ¥${Utils.formatMoney(short)}`); sync(); return; }
        onOk();
    };
    sync();
    return sync;
}

/** 店面共用的打开/关闭口径 */
export interface ShopHandle {
    readonly element: HTMLElement;
    /**
     * onPaid=花钱或换装后刷顶栏；onShown(true) 让宿主把信息栏/周点条压到店面之下；
     * onLeave=店内「离开」钮——收口由宿主统一做（收店面 + 恢复信息栏 + 重算出口钮）。
     */
    open(onPaid: () => void, onShown: (hidden: boolean) => void, onLeave: () => void): void;
    close(): void;
}

/** 部件店（四家共用一类骨架，店名与类目由宿主给） */
export class PartShop implements ShopHandle {
    readonly element: HTMLElement;
    private readonly moneyNum: HTMLElement;
    private readonly hint: HTMLElement;
    private readonly buyBtn: HTMLButtonElement;
    private readonly preview: HTMLElement;
    private readonly tabs: HTMLElement;
    private readonly strip: HTMLElement;
    private readonly nudgeRow: HTMLElement;
    private editor: AvatarEditor | null = null;
    private onPaid: () => void = () => { };
    private onLeave: () => void = () => this.close();
    private readonly cats: FaceCategory[];

    constructor(private readonly title: string, catKeys: string[]) {
        this.cats = FACE_CATEGORIES.filter(c => catKeys.includes(c.key as string));

        const root = createDiv('fr-shop-full');
        const inner = createDiv('studio-inner');
        this.moneyNum = buildShopHead(inner, title);

        const stage = createDiv('studio-stage');
        this.preview = createDiv('cc-preview studio-preview');
        stage.appendChild(this.preview);
        inner.appendChild(stage);

        const controls = createDiv('cc-controls');
        this.tabs = createDiv('cc-tabs');
        this.strip = createDiv('cc-strip');
        // 鼻/嘴上下微调滑杆容器（仅鼻子/嘴巴类目显示；滑杆本体由 AvatarEditor 建，开局页同位同款）
        this.nudgeRow = createDiv('cc-nudge-row');
        controls.appendChild(this.tabs);
        controls.appendChild(this.strip);
        controls.appendChild(this.nudgeRow);

        const foot = createDiv('fr-shop-foot');
        this.hint = createSpan('fr-shop-hint');
        this.buyBtn = createButton('pool-cta', '购买并上身', () => { });
        foot.appendChild(this.hint);
        foot.appendChild(this.buyBtn);
        foot.appendChild(createButton('pool-cta ghost', '离开', () => this.onLeave()));
        controls.appendChild(foot);

        inner.appendChild(controls);
        root.appendChild(inner);
        this.element = root;
    }

    open(onPaid: () => void, onShown: (hidden: boolean) => void, onLeave: () => void): void {
        this.onPaid = onPaid;
        this.onLeave = onLeave;
        const state = GameState.getInstance();
        const draft = sanitizeConfig(state.player.avatar ?? null);
        if (!this.editor) {
            this.editor = new AvatarEditor({
                categories: this.cats,
                preview: this.preview,
                tabs: this.tabs,
                strip: this.strip,
                nudgeRow: this.nudgeRow,
                badge: (cat, i) => state.ownsPart(cat.key as never, i)
                    ? { cls: 'fr-price own', text: '已拥有' }
                    : { cls: 'fr-price', text: priceTag(priceOf(cat.key)) },
                onChange: (cfg, cause) => this.onChange(cfg, cause),
            }, draft);
        } else {
            this.editor.setConfig(draft);
        }
        this.editor.rerender();
        this.refreshFoot();
        // 精灵未就绪时预览与条内小图只有底色，加载完成后补渲一次
        ensureSprites().then(() => this.editor?.rerender()).catch(() => { });
        this.element.classList.add('show');
        onShown(true);
    }

    close(): void {
        this.element.classList.remove('show');
    }

    /**
     * 换件/微调的落档判据（茄子定的交互，不许改成整张草稿直写）：本次动的槽是已拥有件才落——
     * pick 看被点的这件，nudge 看当前类目正在穿的件；预览未拥有件时拨滑杆只进草稿，购买才随整脸落。
     * 落档动作交给 `wearDraft`：逐槽按 owned 过滤，未买的那件回退现值。
     */
    private onChange(cfg: AvatarConfig, cause: 'pick' | 'nudge'): void {
        const state = GameState.getInstance();
        const editor = this.editor!;
        const dim = cause === 'pick'
            ? editor.lastPick
            : { cat: editor.activeCat, index: cfg[editor.activeCat.key] as number };
        if (dim && state.ownsPart(dim.cat.key as never, dim.index)) {
            state.wearDraft(cfg);
            state.warmAvatarCache();
            this.onPaid();
        }
        this.refreshFoot();
    }

    /** 脚部状态槽：没选=留空（衣片的价格签与按压反馈已表达可点）；选中已拥有=已上身（购买钮不出现）；选中未拥有=价签购买 */
    private refreshFoot(): void {
        const state = GameState.getInstance();
        const editor = this.editor!;
        this.moneyNum.textContent = moneyText();
        const picked = editor.getPicked();
        if (!picked) {
            this.hint.textContent = '';
            this.buyBtn.style.display = 'none';
            return;
        }
        if (state.ownsPart(picked.cat.key as never, picked.index)) {
            this.hint.textContent = '已拥有 · 已上身';
            this.buyBtn.style.display = 'none';
            return;
        }
        this.hint.textContent = '';
        this.buyBtn.style.display = '';
        const price = priceOf(picked.cat.key);
        this.buyBtn.textContent = `购买并上身 ¥${Utils.formatMoney(price)}`;
        const doBuy = () => {
            const res = state.buyPart(picked.cat.key as never, picked.index, price);
            if (!res.ok) { showHint(res.reason ?? '无法购买'); return; }
            // buyPart 只补丁写一槽；上身走 wearDraft（刚买这件已在 owned 里，其余未买预览槽照旧回退）
            state.wearDraft(editor.cfg);
            state.warmAvatarCache();
            // 不落盘（月存口径 2026-11-03）：钱货两讫等月初那一格捎带；月内退出=重玩本月，重买即可
            this.onPaid();
            editor.rerender();   // 角标「未拥有→已拥有」+ 高亮刷新
            this.refreshFoot();
        };
        wirePayButton(this.buyBtn, price, doBuy);
    }
}

/** 心意礼品店（12 件平铺三列图格；买断=消耗品进背包，好感档与喜好 ×2 结算在 SocialSystem.gift） */
export class GiftShop implements ShopHandle {
    readonly element: HTMLElement;
    private readonly moneyNum: HTMLElement;
    private readonly list: HTMLElement;
    private onPaid: () => void = () => { };
    private onLeave: () => void = () => this.close();
    /** 每颗购买钮的置灰同步闭包：买掉一件后钱变了，其余钮要跟着当前余额重判 */
    private syncers: (() => void)[] = [];

    constructor() {
        const root = createDiv('fr-shop-full');
        const inner = createDiv('studio-inner');
        this.moneyNum = buildShopHead(inner, LOCATION_MAP.shop_gift.name);
        this.list = createDiv('fr-gift-list');
        inner.appendChild(this.list);
        const foot = createDiv('fr-shop-foot');
        foot.appendChild(createButton('pool-cta ghost', '离开', () => this.onLeave()));
        inner.appendChild(foot);
        root.appendChild(inner);
        this.element = root;
    }

    open(onPaid: () => void, onShown: (hidden: boolean) => void, onLeave: () => void): void {
        this.onPaid = onPaid;
        this.onLeave = onLeave;
        this.render();
        this.element.classList.add('show');
        onShown(true);
    }

    close(): void {
        this.element.classList.remove('show');
    }

    /** 12 件平铺不分分类（2026-10-26 茄子令）；好感与背包数量不上屏 */
    private render(): void {
        const state = GameState.getInstance();
        this.moneyNum.textContent = moneyText();
        this.list.innerHTML = '';
        this.syncers = [];
        GIFTS.forEach(g => {
            const card = createDiv('fr-gift-card');
            // 图位现在是 emoji 占位；真配图按 98×73.5（4:3）贴进 .fr-gift-art
            card.appendChild(createDiv('fr-gift-art', [g.emoji]));
            card.appendChild(createSpan('fr-gift-name', g.name));
            card.appendChild(createSpan('fr-gift-price', `¥${Utils.formatMoney(g.price)}`));
            const buy = createButton('pool-cta', '购买', () => { });
            const doBuy = () => {
                state.addMoney(-g.price);
                state.addItems(g.id, 1);
                // 不落盘（月存口径 2026-11-03）：扣款与进包等月初那一格捎带
                this.onPaid();
                this.moneyNum.textContent = moneyText();
                this.syncers.forEach(fn => fn());
            };
            this.syncers.push(wirePayButton(buy, g.price, doBuy));
            card.appendChild(buy);
            this.list.appendChild(card);
        });
    }
}
